import React, { useEffect, useState, useRef, useCallback, useContext } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Grid, Tooltip, Button } from '@material-ui/core';
import { Panel, BackButton } from '../../../../../Components';
import { CompanyUsers } from '../Users/Users.List';
import { ProcessList } from './Processes';
import { AspectList } from './Aspects';
import { TagList } from './Tags';
import { EditNameInline } from '../EditName';
import { LogotypeForm } from '../LogotypeForm';
import { UserContext } from '../../../../../Context/UserContext/UserContext';
import { useAptorApi } from '../../../../../Api';
import { AptorApi, INamedEntity } from '../../../../../Api/AptorApi';
import { FormValues } from '../../../../../Components/Form/Form.types';
import { useStyles } from "../Organization.styles";
import { ArrowUpward } from '@material-ui/icons';

interface ICompanyUnit extends INamedEntity {
  logotype: string;
}

export const CompanyUnitEditForm = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const id = parseInt(idParam || '0');
  const navigate = useNavigate();
  const location = useLocation();
  const { api, abortController } = useAptorApi();
  const { formatMessage } = useIntl();
  const { getCompanyUnitName, getProcessName, getAspectName, getTagName } = useContext(UserContext);
  const initFlag = useRef(false);
  const [unitName, setUnitName] = useState<string>();
  const [logotype, setLogotype] = useState<string>();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const classes = useStyles();

  // Initialization
  const initializeUnit = useCallback(async () => {
    const info = await api.getCompanyUnit<ICompanyUnit>(id);
    initFlag.current = true;
    setUnitName(info.name);
    setLogotype(info.logotype);
  }, [id, api]);

  useEffect(() => {
    if (!initFlag.current) {
      initializeUnit();
    }
  }, [initializeUnit]);

  const submitEditUnit = async (data: FormValues, api: AptorApi,onSuccess: () => void) => {
    await api.updateCompanyUnit(id, data);
    if (abortController.current.signal.aborted) {
      return;
    }
    initializeUnit();
    onSuccess();
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <BackButton
        disableMargin
        link={
          location.pathname.startsWith('/law-portal/settings')
            ? '/law-portal/settings'
            : location.pathname.startsWith('/law-portal/organization')
            ? '/law-portal/organization/company-division'
            : '/organization'
        }
      />
      <Grid container spacing={4} direction="column" justifyContent="space-evenly" wrap="nowrap">
        <Grid item>
          <Panel defaultExpanded accordion titleKey={getCompanyUnitName(formatMessage)}>
            <EditNameInline name={unitName} submit={submitEditUnit} />
          </Panel>
        </Grid>
        <Grid item>
          <Panel titleKey="organization.users" accordion>
            <CompanyUsers companyUnitId={id} />
          </Panel>
        </Grid>
        <Grid item>
           {/* <Panel titleKey="organization.processes" accordion>*/}
          <Panel title={getProcessName(formatMessage)} accordion>
            <ProcessList unitId={id} />
          </Panel>
        </Grid>
        <Grid item>
           {/* <Panel titleKey="organization.aspects" accordion>*/}
          <Panel title={getAspectName(formatMessage)} accordion>
            <AspectList unitId={id} />
          </Panel>
        </Grid>
        <Grid item>
          {/* <Panel titleKey="organization.tags" accordion>*/}
          <Panel title={getTagName(formatMessage)} accordion>
            <TagList unitId={id} />
          </Panel>
        </Grid>
        <Grid item>
          <Panel accordion titleKey="organization.company-units.logotype">
            <LogotypeForm id={id} type="companyUnit" currentLogo={logotype} onLogoChanged={() => initializeUnit()} />
          </Panel>
        </Grid>
      </Grid>
      {showScrollTop && (
        <Tooltip title={formatMessage({ id: "bottom.to.top.btn.tooltip.text", defaultMessage: "Back to top" })}>
          <Button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={classes.topButton}
          >
            <ArrowUpward style={{ fontSize: 20 }} />
          </Button>
        </Tooltip>
      )}
    </>
  );
};